import { ColDef, ColumnState, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";

import {
  aggridHeaderClass,
  blueButtonClass,
} from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import GridSelect from "src/components/common/gridWrapper/Grid_Select";
import ModalWrapper from "src/components/common/modal/ModalWrapper";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {
  defaultColDef,
  getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { showWarning } from "src/utils/toast/toastUtil";

import {
  defaultKSK2130_R01DetailDto,
  KSK2130_R01DetailDto,
} from "./Dto_KSK2130_R01";
import { useKSK2130_R01Store } from "./Store_KSK2130_R01";

const KSK2130_R01_MODAL_COLUMN_STATE_KEY =
  "KSK2130_R01-material-lot-modal-column-state-v1";
const HEADER_NAMES: Record<string, string> = {
  rowIndex: "",
  logicCenter: "물류센터",
  warehouseNm: "창고명",
  itemCd: "품목코드",
  itemNm: "품명",
  itemSpec: "규격",
  itemStatus: "등급",
  itemUnit: "단위",
  lotNo: "LOT 번호",
  lotCreateDt: "최초 입고일",
  lotQty: "현재고",
};

const getKSK2130_R01LotKey = (item: KSK2130_R01DetailDto) =>
  `${item.itemCd}|${item.lotNo}`;

interface KSK2130_R01LotPrefixViolation {
  missingLot: KSK2130_R01DetailDto;
  selectedLaterLot: KSK2130_R01DetailDto;
}

export const getKSK2130_R01LotGroupKey = (lot: KSK2130_R01DetailDto) =>
  JSON.stringify([lot.warehouseCd, lot.itemCd]);

const getKSK2130_R01LotPrefixViolation = (
  canonicalGroupList: KSK2130_R01DetailDto[],
  selectedLotKeySet: Set<string>,
): KSK2130_R01LotPrefixViolation | undefined => {
  let missingLot: KSK2130_R01DetailDto | undefined;

  for (const lot of canonicalGroupList) {
    if (!selectedLotKeySet.has(getKSK2130_R01LotKey(lot))) {
      missingLot ??= lot;
      continue;
    }
    if (missingLot && missingLot.lotCreateDt < lot.lotCreateDt) {
      return { missingLot, selectedLaterLot: lot };
    }
  }

  return undefined;
};

const getFirstKSK2130_R01LotPrefixViolation = (
  canonicalLotGroupMap: Map<string, KSK2130_R01DetailDto[]>,
  selectedLotKeySet: Set<string>,
): KSK2130_R01LotPrefixViolation | undefined => {
  for (const canonicalGroupList of Array.from(canonicalLotGroupMap.values())) {
    const violation = getKSK2130_R01LotPrefixViolation(
      canonicalGroupList,
      selectedLotKeySet,
    );
    if (violation) return violation;
  }

  return undefined;
};

export const Modal_KSK2130_R01 = () => {
  const {
    KSK2130_R01DetailList,
    KSK2130_R01ModalList,
    insertKSK2130_R01DetailList,
    setIsMaterialLotModalVisible,
  } = useKSK2130_R01Store();
  const gridRef = useRef<AgGridReact<KSK2130_R01DetailDto>>(null);
  const [columnState, setColumnState] = useState<ColumnState[] | null>(null);
  const [, setHoveredRowIndex] = useState<number | null>(null);
  const [selectedLotKeyList, setSelectedLotKeyList] = useState<string[]>([]);

  const canonicalLotGroupMap = useMemo(() => {
    const groupMap = new Map<string, KSK2130_R01DetailDto[]>();

    KSK2130_R01ModalList.forEach((lot) => {
      const groupKey = getKSK2130_R01LotGroupKey(lot);
      const groupList = groupMap.get(groupKey);
      if (groupList) {
        groupList.push(lot);
      } else {
        groupMap.set(groupKey, [lot]);
      }
    });

    return groupMap;
  }, [KSK2130_R01ModalList]);

  const { getRowId, createRowClassRules, paddedGridData } =
    useGridSettingContent<KSK2130_R01DetailDto>({
      gridRef,
      key: "valueIndex",
      gridData: KSK2130_R01ModalList,
      defaultGridData: defaultKSK2130_R01DetailDto,
      selectedRow: selectedLotKeyList,
    });
  const getCellStyle = useCallback(
    (params: any, key: string) =>
      onlyTouchGridStyle(key, "logicCenter")(params),
    [],
  );

  const baseColumnDefs = useMemo<ColDef<KSK2130_R01DetailDto>[]>(
    () => [
      ...getDefaultRowIndexColumnConfig(),
      {
        colId: "logicCenter",
        headerName: "물류센터",
        field: "logicCenter",
        width: 100,
        cellStyle: (params) => getCellStyle(params, "logicCenter"),
      },
      {
        colId: "warehouseNm",
        headerName: "창고명",
        field: "warehouseNm",
        width: 120,
        cellStyle: (params) => getCellStyle(params, "warehouseNm"),
      },
      { colId: "itemCd", headerName: "품목코드", field: "itemCd", width: 150,
        cellStyle: (params) => getCellStyle(params, "itemCd") },
      { colId: "itemNm", headerName: "품명", field: "itemNm", width: 180,
        cellStyle: (params) => getCellStyle(params, "itemNm") },
      { colId: "itemSpec", headerName: "규격", field: "itemSpec", width: 150,
        cellStyle: (params) => getCellStyle(params, "itemSpec") },
      {
        colId: "itemStatus",
        headerName: "등급",
        field: "itemStatus",
        width: 70,
        cellStyle: (params) => getCellStyle(params, "itemStatus"),
      },
      { colId: "itemUnit", headerName: "단위", field: "itemUnit", width: 60,
        cellStyle: (params) => getCellStyle(params, "itemUnit") },
      { colId: "lotNo", headerName: "LOT 번호", field: "lotNo", width: 160,
        cellStyle: (params) => getCellStyle(params, "lotNo") },
      {
        colId: "lotCreateDt",
        headerName: "최초 입고일",
        field: "lotCreateDt",
        width: 110,
        cellStyle: (params) => getCellStyle(params, "lotCreateDt"),
      },
      {
        colId: "lotQty",
        headerName: "현재고",
        field: "lotQty",
        width: 100,
        type: "numericColumn",
        cellStyle: (params) => getCellStyle(params, "lotQty"),
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toLocaleString() : "",
      },
    ],
    [getCellStyle],
  );

  const columnDefs = useMemo<ColDef<KSK2130_R01DetailDto>[]>(() =>
    GridCustomColumnDefs<KSK2130_R01DetailDto>({
      baseColumnDefs,
      columnState: columnState?.some((state) => state.colId === "rowIndex")
        ? columnState
        : null,
      headerNames: HEADER_NAMES,
      fallbackHeaderClass: aggridHeaderClass,
    }) as ColDef<KSK2130_R01DetailDto>[], [baseColumnDefs, columnState]);

  const rowClassRules = useMemo(
    () =>
      createRowClassRules<KSK2130_R01DetailDto>({
        idKey: "valueIndex",
        selectedList: KSK2130_R01ModalList
          .filter((item) => selectedLotKeyList.includes(getKSK2130_R01LotKey(item)))
          .map((item) => item.valueIndex),
        deletedList: [],
      }),
    [createRowClassRules, KSK2130_R01ModalList, selectedLotKeyList],
  );

  const handleRowClick = useCallback(
    (event: RowClickedEvent<KSK2130_R01DetailDto>) => {
      if (event.node.rowPinned === "bottom" || !event.data?.lotNo) return;

      const clickedLot = event.data;
      const lotKey = getKSK2130_R01LotKey(clickedLot);
      const canonicalGroupList =
        canonicalLotGroupMap.get(getKSK2130_R01LotGroupKey(clickedLot)) ?? [];
      const isSelected = selectedLotKeyList.includes(lotKey);
      const nextSelectedLotKeySet = new Set(selectedLotKeyList);

      if (isSelected) {
        nextSelectedLotKeySet.delete(lotKey);
      } else {
        nextSelectedLotKeySet.add(lotKey);
      }

      const violation = getKSK2130_R01LotPrefixViolation(
        canonicalGroupList,
        nextSelectedLotKeySet,
      );
      if (violation) {
        if (isSelected) {
          showWarning(
            `후순위 LOT(${violation.selectedLaterLot.lotNo})이 선택되어 있어 해제할 수 없습니다.`,
          );
        } else {
          showWarning(
            `선행 LOT(${violation.missingLot.lotNo})을 먼저 선택하세요.`,
          );
        }
        return;
      }

      setSelectedLotKeyList(Array.from(nextSelectedLotKeySet));
    },
    [canonicalLotGroupMap, selectedLotKeyList],
  );

  const handleAppendClick = () => {
    const selectedList = KSK2130_R01ModalList.filter((item) =>
      selectedLotKeyList.includes(getKSK2130_R01LotKey(item)),
    );
    if (selectedList.length === 0) {
      showWarning("투입할 LOT을 선택해 주세요.");
      return;
    }

    const prefixViolation = getFirstKSK2130_R01LotPrefixViolation(
      canonicalLotGroupMap,
      new Set(selectedLotKeyList),
    );
    if (prefixViolation) {
      showWarning(
        `선행 LOT(${prefixViolation.missingLot.lotNo})을 먼저 선택하세요.`,
      );
      return;
    }

    const detailKeySet = new Set(
      KSK2130_R01DetailList.map(getKSK2130_R01LotKey),
    );
    insertKSK2130_R01DetailList(
      selectedList.filter((item) => !detailKeySet.has(getKSK2130_R01LotKey(item))),
    );
    setSelectedLotKeyList([]);
    setIsMaterialLotModalVisible(false);
  };

  const handleCloseClick = () => {
    setSelectedLotKeyList([]);
    setIsMaterialLotModalVisible(false);
  };

  return (
    <ModalWrapper
      size="qhmd"
      defaultWidth={1100}
      overlay
      onOverlayClick={handleCloseClick}
      onEscKeyDown={handleCloseClick}
    >
      <div className="w-full h-[8%] flex items-center px-6 bg-gray-100 font-bold">
        BOM 기준 자재 LOT 선택
      </div>
      <div className="w-full h-[78%] min-h-0 px-6 pt-1 flex flex-col ag-theme-alpine">
        <GridContextWrapper<KSK2130_R01DetailDto>
          gridRef={gridRef}
          paddedGridData={paddedGridData}
          initSelected={() => setSelectedLotKeyList([])}
          defaultRowData={defaultKSK2130_R01DetailDto}
          baseColumnDefs={columnDefs}
          setColumnState={setColumnState}
          localStorageKey={KSK2130_R01_MODAL_COLUMN_STATE_KEY}
          enableDataHistory={false}
        >
          <GridSelect<KSK2130_R01DetailDto>
            ref={gridRef}
            rowData={paddedGridData}
            getRowId={getRowId}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowClassRules={rowClassRules}
            onRowClicked={handleRowClick}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey={KSK2130_R01_MODAL_COLUMN_STATE_KEY}
            setColumnState={setColumnState}
            overlayNoRowsTemplate={
              '<span style="padding:32px;">투입 가능한 BOM 자재 LOT이 없습니다.</span>'
            }
          />
        </GridContextWrapper>
      </div>
      <div className="w-full h-[10%] flex items-center px-6 gap-2">
        <button
          className={`${blueButtonClass} text-white font-semibold px-4 rounded h-[30px]`}
          onClick={handleAppendClick}
        >
          선택 항목 담기
        </button>
        <button className="border px-4 rounded h-[30px]" onClick={handleCloseClick}>
          닫기
        </button>
      </div>
    </ModalWrapper>
  );
};
